import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../layouts/AppShell";
import { appModules } from "../components/global";
import { GET, POST, DateRangePicker, toFixed, total, alert, loading, DateInput } from "../components/CustomComponent";

/**
 * หน้าสร้างรอบเงินเดือน (ออกเงินเดือน)
 * - ด้านบน: กำหนดช่วงวันที่ของรอบเงินเดือน + วันที่จ่าย + ชื่อรอบ
 * - ด้านล่าง: ตารางพนักงานสถานะ "ทำงานปกติ" ตั้งต้นจากข้อมูลพนักงาน แก้ไขรายการเงินได้/รายการหักได้
 *
 * NOTE: ยังไม่เชื่อม backend — ใช้ข้อมูล mock เพื่อรีวิว UI เท่านั้น
 */

// คอลัมน์ตัวเลข — tone กำหนดสี (earn = เงินได้ เขียว, deduct = รายการหัก แดง)
// computed = คำนวณอัตโนมัติจาก rate ของพนักงาน (แก้ไขเองไม่ได้)
// สถานะพนักงานที่นำเข้ารอบเงินเดือน (ตรงกับตัวเลือกในหน้าพนักงาน)
const ACTIVE_EMPLOYEE_STATUS = "ทำงานปกติ";

const earningCols = [
  { key: "salary", label: "เงินเดือน", tone: "earn" },
  { key: "overtime", label: "ค่าล่วงเวลา", tone: "earn" },
  { key: "bonus", label: "โบนัส", tone: "earn" },
];
const deductionCols = [
    { key: "wht", label: "ภาษีหัก ณ ที่จ่าย", tone: "deduct", computed: true },
    { key: "sso", label: "ประกันสังคม", tone: "deduct", computed: true },
    { key: "other", label: "อื่นๆ", tone: "deduct" },
];
const numberCols = [...earningCols, ...deductionCols];

const toNumber = (v) => Number(String(v ?? "").replace(/,/g, "")) || 0;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// คำนวณรายการหักอัตโนมัติจาก rate ของพนักงาน
// - ภาษีหัก ณ ที่จ่าย = เงินได้รวม (เงินเดือน+ค่าล่วงเวลา+โบนัส) × wht_rate%
// - ประกันสังคม = min(เงินเดือน, เพดาน) × sso_rate%  (เฉพาะผู้เข้าร่วมประกันสังคม)
const computeAutoDeductions = (r) => {
  const earn = Number(r.salary || 0) + Number(r.overtime || 0) + Number(r.bonus || 0);
  const wht = round2((earn * Number(r.wht_rate || 0)) / 100);
  const ssoBase = Math.min(Number(r.salary || 0), Number(r.sso_ceiling || 0) || Number(r.salary || 0));
  const sso = r.is_sso ? round2((ssoBase * Number(r.sso_rate || 0)) / 100) : 0;
  return { wht, sso };
};

// แปลงพนักงานจาก API → แถวในตาราง (ตั้งเงินเดือน + rate จากข้อมูลพนักงาน แล้วคำนวณรายการหักให้)
const mapEmployeeToRow = (e) => {
  const fullName = `${e.first_name || ""} ${e.last_name || ""}`.trim();
  const row = {
    id: e.uid,
    name: fullName || e.nickname || e.employee_code || "-",
    position: e.position || "-",
    salary: toNumber(e.salary),
    overtime: 0,
    bonus: 0,
    other: 0,
    // rate จากโปรไฟล์พนักงาน (ใช้คำนวณ ไม่แสดงเป็นคอลัมน์)
    wht_rate: toNumber(e.wht_rate),
    sso_rate: toNumber(e.sso_rate),
    sso_ceiling: toNumber(e.sso_wage_ceiling) || 17500,
    is_sso: e.is_social_security !== false,
  };
  return { ...row, ...computeAutoDeductions(row) };
};

// ค่าเริ่มต้นของรอบเงินเดือน: วันที่ 1-25, จ่ายวันที่ 26, ชื่อรอบตามเดือน/ปีปัจจุบัน
const thaiMonths = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const pad2 = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`; // m = 0-based
// แปลง Date → "YYYY-MM-DD" (ใช้เวลาท้องถิ่น เลี่ยงปัญหา timezone ของ toISOString)
const dateToYmd = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return ymd(d.getFullYear(), d.getMonth(), d.getDate());
};
const buildDefaults = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  return {
    period: { start_date: new Date(y, m, 1), end_date: new Date(y, m, 25) },
    payDate: ymd(y, m, 26),
    roundName: `เงินเดือนงวด${thaiMonths[m]} ${y}`,
  };
};

const sumKeys = (r, cols) => cols.reduce((s, c) => s + Number(r[c.key] || 0), 0);
const earnings = (r) => sumKeys(r, earningCols);
const deductions = (r) => sumKeys(r, deductionCols);
const netTotal = (r) => earnings(r) - deductions(r);

const avatarColor = (name) => {
  const colors = ["#0c75e6", "#2fd5e4", "#e5a93e", "#9b6dff", "#34c38f", "#f46a6a"];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return colors[sum % colors.length];
};

// อินพุตตัวเลขที่หน้าตาเหมือนช่องตาราง (borderless) แต่แก้ไขได้
// earn = เงินได้ (เขียว), deduct = รายการหัก (แดง)
const CellInput = ({ value, onChange, tone }) => {
  const color = tone === "deduct" ? "#e54848" : "#2ec4b6";
  return (
    <input
      type="number"
      min="0"
      step="0.01"
      value={value === 0 ? "" : value}
      placeholder="0.00"
      onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
      onFocus={(e) => e.target.select()}
      className="payroll-cell-input border text-end small"
      style={{ color, fontWeight: 600 }}
    />
  );
};

// ช่องคำนวณอัตโนมัติ (อ่านอย่างเดียว) — ใช้กับ ภาษีหัก ณ ที่จ่าย / ประกันสังคม
const CellAuto = ({ value, title }) => (
  <div
    className="payroll-cell-auto text-end small"
    title={title}
    style={{ color: "#e54848", fontWeight: 600 }}
  >
    {toFixed(value)}
  </div>
);

export default function PayrollCreate() {
  const navigate = useNavigate();
  const { title, description } = appModules["payroll_create"];

  const defaults = useMemo(() => buildDefaults(), []);
  const [period, setPeriod] = useState(defaults.period);
  const [payDate, setPayDate] = useState(defaults.payDate);
  const [roundName, setRoundName] = useState(defaults.roundName);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // ตั้งต้นเฉพาะพนักงานที่ยังทำงานอยู่จริง (ตัดทดลองงาน / ลาออก / พักงาน ออก)
  const getEmployees = async () => {
    loading(true);
    const result = await GET("v1/admin/employees", { page: 0, page_size: 1000, data_search: "", status: ACTIVE_EMPLOYEE_STATUS });
    if (result?.status) {
      setRows((result.data || []).map(mapEmployeeToRow));
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถดึงข้อมูลพนักงานได้");
    }
    setLoaded(true);
    loading(false);
  };

  useEffect(() => {
    getEmployees();
  }, []);

  const updateCell = (id, key, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [key]: value };
        // แก้เงินเดือน/ค่าล่วงเวลา/โบนัส แล้วคำนวณ ภาษีหัก ณ ที่จ่าย + ประกันสังคม ใหม่
        return { ...next, ...computeAutoDeductions(next) };
      })
    );
  };

  // เอาพนักงานออกจากรอบเงินเดือน (มี confirm)
  const removeRow = async (row) => {
    if (await alert("", "warning", "นำออกจากรอบเงินเดือน", `ต้องการนำ "${row.name}" ออกจากรอบเงินเดือนนี้หรือไม่?`, "นำออก", "ยกเลิก")) {
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    }
  };

  const summary = useMemo(() => {
    const base = {
      employees: rows.length,
      net: total(rows.map(netTotal)),
      earn: total(rows.map(earnings)),
    };
    numberCols.forEach((c) => {
      base[c.key] = total(rows, c.key);
    });
    return base;
  }, [rows]);

  const handleSave = async () => {
    if (!period.start_date || !period.end_date) {
      return alert("", "warning", "แจ้งเตือน", "กรุณาเลือกช่วงวันที่ของรอบเงินเดือน");
    }
    if (!rows.length) {
      return alert("", "warning", "แจ้งเตือน", "ไม่มีรายการพนักงานในรอบเงินเดือน");
    }
    if (await alert("", "info", "ยืนยัน", "ยืนยันการสร้างรอบเงินเดือนนี้ใช่หรือไม่?", "ยืนยัน", "ยกเลิก")) {
      loading(true);
      const body = {
        name: roundName || undefined,
        period_start: dateToYmd(period.start_date),
        period_end: dateToYmd(period.end_date),
        pay_date: payDate || undefined,
        // ส่งเฉพาะ field ที่ DTO รับ (ตัด rate/metadata ที่ใช้คำนวณฝั่ง UI ออก)
        items: rows.map((r) => ({
          employee_id: r.id,
          employee_name: r.name,
          position: r.position,
          salary: Number(r.salary) || 0,
          overtime: Number(r.overtime) || 0,
          bonus: Number(r.bonus) || 0,
          wht: Number(r.wht) || 0,
          sso: Number(r.sso) || 0,
          other: Number(r.other) || 0,
        })),
      };
      const result = await POST("v1/admin/payrolls", body);
      loading(false);
      if (result?.status) {
        await alert("", "success", "สำเร็จ", "สร้างรอบเงินเดือนสำเร็จ (สถานะ: รออนุมัติ)");
        navigate("/payroll");
      } else {
        alert("", "warning", "แจ้งเตือน", result?.message || "ไม่สามารถสร้างรอบเงินเดือนได้");
      }
    }
  };

  return (
      <AppShell title={title} description={description}>
          {/* แถบหัว: ปุ่มย้อนกลับ + ชื่อหน้า */}
          <div className="d-flex align-items-center gap-3 mb-4">
              <button onClick={() => navigate("/payroll")} className="btn btn-light rounded-3 border" title="ย้อนกลับ">
                  <i className="fa-solid fa-arrow-left" />
              </button>
              <div>
                  <h4 className="fw-bold mb-0 text-dark">สร้างรอบเงินเดือน</h4>
                  <span className="small text-secondary">{description}</span>
              </div>
          </div>

          {/* ส่วนกำหนดรอบเงินเดือน */}
          <div className="rounded-4 p-4 mb-4 bg-white" style={{ border: "1px solid rgba(18, 125, 255, 0.10)", boxShadow: "0 4px 12px rgba(12, 118, 229, 0.04)" }}>
              <h6 className="fw-semibold text-dark mb-3">
                  <i className="fa-regular fa-calendar me-2 text-primary" />
                  ข้อมูลรอบเงินเดือน
              </h6>
              <div className="row g-3">
                  <div className="col-12 col-lg-5">
                      <label className="form-label small text-secondary">ช่วงวันที่ของรอบเงินเดือน *</label>
                      <DateRangePicker className="form-control" value={period} onChange={(val) => setPeriod(val)} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                      <label className="form-label small text-secondary">วันที่จ่ายเงิน</label>
                      <DateInput className="form-control" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                      <label className="form-label small text-secondary">ชื่อรอบ (ไม่บังคับ)</label>
                      <input type="text" className="form-control" placeholder="เช่น เงินเดือนงวดมิถุนายน 2026" value={roundName} onChange={(e) => setRoundName(e.target.value)} />
                  </div>
              </div>
          </div>

          {/* ตารางพนักงาน */}
          <div className="rounded-4 p-3 bg-white" style={{ border: "1px solid rgba(18, 125, 255, 0.10)", boxShadow: "0 4px 12px rgba(12, 118, 229, 0.04)" }}>
              <div className="table-responsive">
                  <table className="table align-middle payroll-table mb-0">
                      <thead>
                          <tr className="text-secondary">
                              <th className="text-start" style={{ minWidth: 220 }}>
                                  ชื่อพนักงาน
                              </th>
                              <th className="text-end" style={{ minWidth: 130 }}>
                                  ยอดรวม
                              </th>
                              {numberCols.map((c) => (
                                  <th key={c.key} className={`text-end ${c.tone === "deduct" ? "text-danger" : "text-success"}`} style={{ minWidth: 120 }}>
                                      {c.label}
                                      {c.computed && <i className="fa-solid fa-calculator ms-1" title="คำนวณอัตโนมัติจาก" style={{ fontSize: ".7rem", opacity: 0.7 }} />}
                                  </th>
                              ))}
                              <th className="text-center" style={{ width: 56 }}></th>
                          </tr>
                      </thead>
                      <tbody>
                          {loaded && rows.length === 0 && (
                              <tr>
                                  <td colSpan={3 + numberCols.length} className="text-center text-secondary py-5">
                                      <i className="fa-regular fa-folder-open me-2" />
                                      ไม่พบข้อมูลพนักงาน
                                  </td>
                              </tr>
                          )}
                          {rows.map((r) => (
                              <tr key={r.id}>
                                  {/* ชื่อ + ตำแหน่ง + avatar */}
                                  <td className="text-start">
                                      <div className="d-flex align-items-center gap-2">
                                          <div
                                              className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                              style={{ width: 40, height: 40, background: avatarColor(r.name), fontWeight: 600 }}
                                          >
                                              {r.name.trim().charAt(0).toUpperCase()}
                                          </div>
                                          <div className="text-truncate">
                                              <div className="fw-semibold text-primary text-truncate" style={{ maxWidth: 160 }}>
                                                  {r.name}
                                              </div>
                                              <div className="small text-secondary">{r.position}</div>
                                          </div>
                                      </div>
                                  </td>

                                  {/* ยอดรวม (คำนวณ) */}
                                  <td className="text-end">
                                      <div className="small text-success">{toFixed(earnings(r))}</div>
                                      <div className="small text-danger">-{toFixed(deductions(r))}</div>
                                      <div className="small text-dark">{toFixed(netTotal(r))}</div>
                                  </td>

                                  {/* เงินได้ (เขียว) + รายการหัก (แดง) — wht/sso คำนวณอัตโนมัติ */}
                                  {numberCols.map((c) => (
                                      <td key={c.key}>
                                          {c.computed ? (
                                              <CellAuto
                                                  value={r[c.key]}
                                                  title={
                                                      c.key === "wht"
                                                          ? `คำนวณจาก ภาษีหัก ณ ที่จ่าย ${toNumber(r.wht_rate)}%`
                                                          : r.is_sso
                                                              ? `คำนวณจาก ประกันสังคม ${toNumber(r.sso_rate)}% (เพดาน ${toFixed(r.sso_ceiling)})`
                                                              : "พนักงานไม่เข้าร่วมประกันสังคม"
                                                  }
                                              />
                                          ) : (
                                              <CellInput value={r[c.key]} tone={c.tone} onChange={(v) => updateCell(r.id, c.key, v)} />
                                          )}
                                      </td>
                                  ))}
                                  <td className="text-center">
                                      <button
                                          type="button"
                                          onClick={() => removeRow(r)}
                                          className="btn btn-sm btn-light text-danger rounded-circle"
                                          title="นำออกจากรอบเงินเดือน"
                                          style={{ width: 32, height: 32, lineHeight: 1 }}
                                      >
                                          <i className="fa-solid fa-trash-can" />
                                      </button>
                                  </td>
                              </tr>
                          ))}
                      </tbody>
                      <tfoot>
                          <tr className="payroll-foot fw-bold">
                              <td className="text-start">
                                  {summary.employees} <span className="text-secondary fw-normal">คน</span>
                              </td>
                              <td className="text-end text-dark">{toFixed(summary.net)}</td>
                              {numberCols.map((c) => (
                                  <td key={c.key} className={`text-end ${c.tone === "deduct" ? "text-danger" : "text-success"}`}>
                                      {toFixed(summary[c.key])}
                                  </td>
                              ))}
                              <td></td>
                          </tr>
                      </tfoot>
                  </table>
              </div>
          </div>

          {/* ปุ่มดำเนินการ */}
          <div className="d-flex flex-column flex-sm-row justify-content-sm-end gap-2 mt-4">
              <button onClick={() => navigate("/payroll")} className="btn btn-secondary px-4">
                  ยกเลิก
              </button>
              <button onClick={handleSave} className="btn btn-primary px-4">
                  <i className="fa-solid fa-check me-2" />
                  บันทึกรอบเงินเดือน
              </button>
          </div>

          <style>
              {`
          .payroll-table thead th { font-weight: 600; font-size: .85rem; border-bottom: 1px solid #eef2f6; white-space: nowrap; }
          .payroll-table tbody td { border-bottom: 1px solid #f3f6f9; padding-top: .65rem; padding-bottom: .65rem; }
          .payroll-cell-input {
            width: 100%; border: 1px solid transparent; background: #f7fafc;
            border-radius: 8px; padding: .4rem .55rem; outline: none; transition: all .15s ease;
          }
          .payroll-cell-input:hover { background: #eef4fb; }
          .payroll-cell-input:focus { background: #fff; border-color: #0c75e6; box-shadow: 0 0 0 3px rgba(12,117,230,.12); }
          /* ซ่อนลูกศร spinner ของ number input */
          .payroll-cell-input::-webkit-outer-spin-button,
          .payroll-cell-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
          .payroll-cell-input { -moz-appearance: textfield; }
          /* ช่องคำนวณอัตโนมัติ (อ่านอย่างเดียว) — มีลายทางจาง ๆ บอกว่าแก้ไขไม่ได้ */
          .payroll-cell-auto {
            width: 100%; padding: .4rem .55rem; border-radius: 8px;
            background: repeating-linear-gradient(45deg, #fbf1f1, #fbf1f1 6px, #fdf6f6 6px, #fdf6f6 12px);
            border: 1px solid #f3dede;
          }
          .payroll-foot td { border-top: 2px solid #e9eef3; background: #fafcfe; padding-top: .85rem; padding-bottom: .85rem; }
        `}
          </style>
      </AppShell>
  );
}
